import React, { Suspense } from 'react';
import { lazyWithRetry, handleChunkRejectionEvent, handleChunkScriptErrorEvent } from './utils/lazyWithRetry';
import { initStaleTabSelfHeal } from './utils/staleTabSelfHeal';
import ResilientSuspenseFallback from './components/ResilientSuspenseFallback';
// A guard's loading screen waits in the light of the page it guards (D-92 part 3).
import { GuardLoading } from './components/routing/GuardLoading';
import { logLoadingGate, LOADING_GATE_DEFAULT_TIMEOUT_MS } from './utils/loadingGate';
import { Helmet } from 'react-helmet-async';
import { Routes, Route, useLocation } from 'react-router-dom';
// Every <Navigate> here keeps the visitor's light while it moves (D-92 part 6): React Router's draws nothing, so
// the document's ground fell to noon until the next page said its light.
import { NavigateInTheLight as Navigate } from './components/routing/NavigateInTheLight';
import styled from 'styled-components';
import ErrorBoundary from './components/ErrorBoundary';
import { withSafeAnimation } from './components/SafeAnimationWrapper';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './context/AuthContext';
import { useConfirmedAuth } from './hooks/useConfirmedAuth';
import { useRbac } from './hooks/useRbac';
import { useDeveloperOrg } from './hooks/useDeveloperOrg';
import { AITransactionProvider } from './context/AITransactionContext';
import { OLVRateProvider } from './context/OLVRateContext';
import { ConversationHistoryProvider } from './context/ConversationHistoryContext';
import { WalletSummaryProvider } from './context/WalletSummaryContext';
import { TopUpModalProvider } from './context/TopUpModalContext';
import { GlobalLowBalanceBanner } from './components/wallet/GlobalLowBalanceBanner';
import { PausedMembershipBanner } from './components/common/PausedMembershipBanner';
import { BillingPausedBanner } from './components/common/BillingPausedBanner';
import { TrialStatusBanner } from './components/wallet/TrialStatusBanner';
import { GlobalBannerCenter } from './components/common/GlobalBannerCenter';
import EmailVerifyBanner from './components/common/EmailVerifyBanner';
import AppUpdateBanner from './components/common/AppUpdateBanner';
import InstallAppBanner from './components/common/InstallAppBanner';
import PhoneVerifyBanner from './components/common/PhoneVerifyBanner';
import ProvePhoneBanner from './components/common/ProvePhoneBanner';
import SignInRequirementBanner from './components/common/SignInRequirementBanner';
import { TrialOnboardingGate } from './components/onboarding/TrialOnboardingGate';
import { PendingInviteGate } from './components/onboarding/PendingInviteGate';
import { PendingReferralGate } from './components/onboarding/PendingReferralGate';
import { TermsUpdateHost } from './components/legal/TermsUpdateHost';
import { subscribeToAuthState } from './lib/auth';
import { loadUserContext } from './lib/identity/context';
// The setup-only mark (sign-in requirements, docs/PLAN-signin-requirements-20261029.md): the session gate below.
import { whenSetupKnown } from './lib/signInSecurity/setupSession';
import { useSignInSetupHold, useSignInSetupReading } from './hooks/useSignInSetupSession';
import InAppMessageOverlay from './components/InAppMessageOverlay';
import DesktopSignInModalHost from './components/DesktopSignInModalHost';
import ProgramWorkstationModalHost from './components/ProgramWorkstationModalHost';
import ComposioConnectCoachHost from './components/ComposioConnectCoachHost';
import EmailIntentPromptHost from './components/EmailIntentPromptHost';
import ConnectSetupHost from './components/ConnectSetupHost';
import WorkspaceTransitionHost from './components/WorkspaceTransitionOverlay';
// The chat engine loads a moment after the page paints (PR-S, 2026-09-22):
// never import it — or the chat barrel ('./components/GlobalChatSystem'),
// which re-exports the whole engine — statically here. The loader is the one
// door; scripts/check-chat-first-paint.mjs keeps it that way.
import { ChatEngineLoader } from './components/GlobalChatSystem/ChatEngineLoader';
import {
  DOCKED_COMPANION_MIN_VIEWPORT_PX,
  DOCKED_RAIL_FOOTPRINT_CSS,
} from './components/GlobalChatSystem/dockedCompanion';
import { dockedWidthStyle, useDockedWidthChoice } from './components/GlobalChatSystem/dockedWidth';
import type { TokenUsageSnapshot } from './components/GlobalChatSystem/types';
import { usePageChatConfig } from './hooks/usePageChatConfig';
import { useChatExpandedAddress } from './hooks/useChatExpandedAddress';
import { useTrialOnboardingGate } from './hooks/useTrialOnboardingGate';
import { useMaybeConversationHistory } from './context/ConversationHistoryContext';
import { useTenantChatConfig } from './hooks/useTenantChatConfig';
import { getJustJoinedOrg } from './lib/justJoinedOrg';
// Anonymous auth, the consent banner and the page-tracking hook were deleted
// 2026-09-11: the site sets no analytics or advertising cookie, so there is
// nothing to consent to. See docs/PLAN-legal-compliance-hardening-20260911.md.
// The moonshot demo loads as its own chunk, only on the demo's pages
// (src/moonshot/MoonshotDoor.tsx). Never import the './moonshot' barrel here.
import { DemoMockGate, MoonshotPage } from './moonshot/MoonshotDoor';
import { isMoonshotMode } from './moonshot/useMoonshotMode';
import { flushAllPendingUpdates } from './utils/debouncedFirestoreUpdates';
import { installDebounceAutoFlush } from './repositories/firestoreUtils';
import { installConnectFunnelAppender } from './services/composio/connectFunnelAppender';
// Register test data cleanup utilities on window for console access
import './utils/cleanupTestWalletData';

// EnsureFixedAssets: SYNCHRONOUS import (NOT React.lazy). LandingPage was one too, until 2026-09-25.
//
// Fix for fix/unauthed-landing-page-resilient (2026-05-09 demo-day P0):
// On first mount of `/` in incognito / fresh-cache sessions, both lazy
// boundaries (EnsureFixedAssets Suspense and the Routes Suspense which
// hosts LandingPage) reliably stalled in their fallback even after
// the chunk loaded HTTP 200, the webpack module was registered
// (__webpack_modules__[182] present), and React.lazy's payload._status
// flipped to Resolved (=1). The Suspense memoizedState stayed non-null
// (suspended) and the wake-up retry never fired for the initial mount.
// Navigating away and back caused the boundary to render correctly,
// confirming the lazy itself was healthy and the bug is in the
// initial-mount Suspense retry path under React 18 production /
// concurrent rendering for THIS specific tree shape (lazy directly
// inside an outer Suspense alongside a sibling lazy + complex
// Provider stack above).
//
// LandingPage was the home page — every visitor saw it, so its bundle
// was always loaded on the first paint anyway. Code-splitting it away
// from main.* didn't actually defer download for the common case;
// it only added a Suspense boundary that triggered this retry-stall
// bug. Pulling it (and EnsureFixedAssets, which has the same shape)
// into the main chunk eliminated the failure mode entirely.
//
// 2026-09-25: prest0.ai/ shows the new design's home page (since 09-24), so
// LandingPage is only the fallback, and it left the first download. It loads
// through LandingPageDoor, which uses useDeferredModule — a plain import()
// resolved into state, never React.lazy, so the stall above cannot recur —
// exactly as the new home page's own door has served `/` since 09-24.
// tests/hooks/unauthedLandingResilient.test.ts pins both halves.
//
// Distinctive bundle marker for live-deploy verification:
//   __PREST0_UNAUTHED_LANDING_RESILIENT_2026_05_09__
import LandingPageDoor from './components/LandingPageDoor';
import EnsureFixedAssets from './components/EnsureFixedAssets';
// The new design (Miles, 2026-09-23/24): the surfaces already rebuilt swap in
// under prest0.ai/prest0design/ (the demonstration address, every surface) and
// on prest0.ai itself for the surfaces config/site-design.json lists as live.
// The door is tiny and on the first download; what it opens is its own chunk
// (the `pavilion` row of scripts/check-chat-first-paint.mjs). Where a surface
// is not shown, nothing here changes a byte of what renders.
import { designShows, headWritesNoindex } from './design/previewPath';
import PavilionHomeDoor from './design/pavilion/PavilionHomeDoor';
import { PavilionAppHeaderDoor, withPavilion } from './design/pavilion/withPavilion';
import { useAppChromeClaimed } from './design/pavilion/appChrome';
// deferredRoute: the non-Suspense way to split a route in this tree. The four
// heaviest routes a first-time visitor never lands on (blog 414 KB, onboarding
// 301 KB, wallet 191 KB, subscription 134 KB of source) load after first paint
// instead of before it. See src/components/DeferredRoute.tsx for why this is
// not React.lazy.
import { deferredRoute } from './components/DeferredRoute';
// Header: SYNCHRONOUS import (NOT React.lazy) — demo-day follow-up to PR #76.
//
// PR #76 un-lazy'd LandingPage + EnsureFixedAssets to break the React-18
// initial-mount Suspense retry-stall on `/`. After PRs #67-79 shipped,
// the SAME stall reproduces on every other lazy route (/justiceisurright,
// /team, /mission, /about, /pricing, /security, /privacy-policy,
// /terms-of-service, /citadel, /insights). Headless playwright catches
// the watchdog "this page is taking longer than expected to load" on
// every one of those routes after a 10s wait on a fresh browser context.
//
// PR #76's diagnosis flagged that the bug fires when there are MULTIPLE
// sibling Suspense boundaries with lazy children. The remaining
// `<Suspense fallback={null}><Header/></Suspense>` boundary appears to be
// the trigger that kept the route-level Suspense from waking up on first
// mount. Header is on every page anyway, so folding it into main.* does
// not regress first-paint cost — it only removes one extra Suspense
// round-trip and unblocks the lazy route component below it.
//
// This is a workaround, not a root-cause fix. The right long-term fix is
// either upgrading React to a version that addresses the retry-stall, or
// removing the inner `<Suspense fallback={null}>` boundaries entirely.
// Pinning Header into main.* is the smallest change that unblocks the
// demo today.
//
// Distinctive bundle marker for live-deploy verification:
//   __PREST0_UNLAZY_HEADER_2026_05_09__
import Header from './components/Header';
// Public marketing/legal routes: SYNCHRONOUS imports (NOT React.lazy).
//
// PR #80 un-lazy'd Header + JusticeIsRight, but live verification still
// shows every other public lazy route stuck in the App route-level
// Suspense fallback. The safest demo-day fix is to remove React.lazy
// from the public entry routes that unauthenticated visitors can land on
// directly. Authenticated/private routes remain lazy.
//
// Live-deploy verification marker:
//   __PREST0_UNLAZY_PUBLIC_ROUTES_2026_05_09__
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookie from './pages/Cookie';
import Legal from './pages/Legal';
// The Trust Center is NOT here: it loads when /trust opens (deferredRoute, below the deferred routes' note).
// The 404, the catch-all route's page (it was written inline here until 2026-10-11; its words are notFoundWords.ts).
import NotFound from './pages/NotFound';
// Today's versions of the public pages whose rebuild is live: only fallbacks now, so they load on demand, in one chunk
// (src/components/TodaysPageDoor.tsx); a page joins there when its word goes live, never before.
import { todaysPage } from './components/TodaysPageDoor';
// NOTE (Round M, 2026-07-15): the /citadel web page was deleted entirely per
// owner directive — its former route now falls through to the catch-all 404.
// Internal 'citadel' naming elsewhere (functions/, vm instance ids, chat
// internal-surface source patterns) refers to the internal bot VM surfaces,
// NOT the deleted web page, and stays.
// /organization is NOT here: it loads when its route opens (deferredRoute, below the deferred routes' note).
import UsageLimitBanner from './components/Activity/UsageLimitBanner';
import VmLifecycleBanner from './components/common/VmLifecycleBanner';
import { DemoBandSource } from './demo/DemoDoor';
import AgentActivity from './pages/AgentActivity';
// /integrations is NOT here: it loads when its route opens (deferredRoute, below the deferred routes' note).
import { readConnectPopupLaunch } from './services/composio/ComposioClient';
// Tier-21 interactive 3D preview viewer (2026-08-01). SYNC import per the
// authenticated-page convention above (React-18 route-level Suspense retry stall).
import FloatingPreviewProvider from './components/FilePreview/FloatingPreviewContext';
// /infrastructure and /admin/vms are NOT here: they load when their routes open (deferredRoute, below the deferred routes' note).
// /portal-controls is NOT here: it loads when its route opens (deferredRoute, below the deferred routes' note).
// TeamRolesPage (/settings/team): SYNCHRONOUS import (NOT React.lazy).
//   Until 2026-10-11. Since Team & Roles part 1 the page loads through deferredRoute (below the
//   deferred routes' note), which is not React.lazy either: it renders nothing until the module
//   arrives and mounts no Suspense boundary, so the stall described here cannot return.
//
// /settings/team was the last authenticated account page still lazy-loaded
// after the 2026-05-12 "un-lazy private routes" sweep (which un-lazied
// /desktop, /wallet, /integrations, /onboarding, /subscription, …). It kept
// hitting the same React-18 initial-mount route-level Suspense retry-stall:
// the `team-roles` chunk resolves (HTTP 200, module registered) but the
// route-level Suspense boundary never wakes on first mount, so the fallback
// keeps rendering until ResilientSuspenseFallback's 30 s terminal stage shows
// "this page is taking longer than expected to load. refresh" (the exact
// symptom users reported on this page). Folding the page into main.* removes
// the only suspending child under the route-level Suspense boundary for this
// route, so the stall — and its refresh UI — can no longer fire. Matches the
// proven fix already applied to every other authenticated account route.
//
// Live-deploy verification marker:
//   __PREST0_UNLAZY_TEAM_SETTINGS_2026_07_08__
// /justiceisurright is RETIRED (Miles, 2026-10-24): the partner firm's program page and its account gate are gone, and
// its address forwards to /desktop (the route below). What the page said is the workspace's program record, which
// Prest0n reads (functions/src/prest0nSession/programFacts.ts); tests/routing/justiceRouteRetired.test.ts pins it.

// ── Deferred routes ────────────────────────────────────────────────────────
// The four heaviest routes a first-time visitor never lands on: blog 414 KB,
// onboarding 301 KB, wallet 191 KB, subscription 134 KB of source. They load
// after first paint instead of before it.
//
// deferredRoute is NOT React.lazy — see src/components/DeferredRoute.tsx and
// the note at the top of this file for the 2026-05-09 initial-mount Suspense
// stall that made four PRs tear code-splitting out of this app. No Suspense
// boundary is involved here, so that retry path is never entered.
//
// `/` stays a static import and must: the pin in
// tests/hooks/unauthedLandingResilient.test.ts enforces it.
// /insights is the new design's, drawn from the CMS (insights studio S4); today's design has no insights page since the
// old one was retired (S6): InsightsUnavailable is the minimal page shown if its chunks cannot load.
const InsightsUnavailable = deferredRoute(() => import('./pages/InsightsUnavailable'), '/insights');
// prest0.ai/roadmap and /changelog (docs/PLAN-roadmap-changelog-20261020.md, R5): born in the new design, each its own
// page; /changelog ("what's new") is live since 2026-10-21, /roadmap not yet. RoadmapUnavailable is what today's design
// shows at an address whose page is not live, and the minimal page if a page's chunk cannot load.
const RoadmapUnavailable = deferredRoute(() => import('./pages/RoadmapUnavailable'), '/roadmap');
const Wallet = deferredRoute(() => import('./pages/Wallet'), '/wallet');
const Subscription = deferredRoute(() => import('./pages/Subscription'), '/subscription');
const AgreementUnavailable = deferredRoute(() => import('./pages/AgreementUnavailable'), '/subscription/agreement');
// The trial's one screen (2026-10-20, docs/PLAN-trial-card-on-file-20261020.md): born in the new design too.
const TrialStartUnavailable = deferredRoute(() => import('./pages/TrialStartUnavailable'), '/subscription/trial');
const SignInSetupUnavailable = deferredRoute(() => import('./pages/SignInSetupUnavailable'), '/sign-in-setup');
// The GSD revamp's new public pages in today's design (2026-10-15): a minimal page that points to /pricing.
const GsdPageUnavailable = deferredRoute(() => import('./pages/GsdPageUnavailable'), '/gsd-page-unavailable');
const Onboarding = deferredRoute(() => import('./pages/Onboarding'), '/onboarding');
// /onboarding in the new design (PV14): its focused frame is its registry row's; the rebuild draws the same hook as today's page.
const OnboardingInTheDesign = withPavilion(Onboarding, 'onboarding');
// The Trust Center (PV5c, 2026-10-01): a page a first-time visitor does not land on. Its words
// (trustCenterDocument.tsx), the lists it publishes (the subprocessors, the retention schedule, the
// accessibility scope) and the forget control rode the first download, about 28 KB before compression;
// they load when /trust opens. Today's page is its own file (`trust`); the words, the lists and the forget
// control's state sit in the file it shares with the new design's Trust Center, which loads the same
// modules. scripts/check-chat-first-paint.mjs R4 holds them off the first download (the `trust` row).
const Trust = deferredRoute(() => import(/* webpackChunkName: "trust" */ './pages/Trust'), '/trust');
// prest0.ai/demo (2026-10-13): the booking calendar as a page. Today's page is the new design's fallback, so it
// loads only when /demo opens and its new-design page cannot.
const BookDemo = deferredRoute(() => import(/* webpackChunkName: "demo" */ './pages/BookDemo'), '/demo');
// /settings and /profile (PV5-8:B part 1, 2026-10-10): signed-in pages a first-time visitor never lands on.
// Their behaviour moved into useSettingsPage and useProfilePage, which left main.js about 1 KB under its
// budget; like the wallet and subscription pages they load when their route opens, each its own file.
const Settings = deferredRoute(() => import(/* webpackChunkName: "settings" */ './components/Settings'), '/settings');
const Profile = deferredRoute(() => import(/* webpackChunkName: "profile" */ './components/Profile'), '/profile');
// /activity and /notifications (PV12 part 1, 2026-10-10): signed-in pages a first-time visitor never lands on. Never
// React.lazy (the authenticated-page convention above); like /settings and /profile they load through deferredRoute when
// their route opens, each its own file, now that their behaviour lives in useActivityPage and useNotificationsPage.
const ActivityPage = deferredRoute(() => import(/* webpackChunkName: "activity" */ './pages/Activity'), '/activity');
const Notifications = deferredRoute(() => import(/* webpackChunkName: "notifications" */ './pages/Notifications'), '/notifications');
// /notifications in the new design (PV12): it stands under the app header, and its rebuild draws from the same hook as today's page.
const NotificationsInTheDesign = withPavilion(Notifications, 'notifications', { chrome: 'app' });
// /activity in the new design (PV12 part 2): it stands under the app header, and its rebuild draws from the same hooks as today's page.
const ActivityInTheDesign = withPavilion(ActivityPage, 'activity', { chrome: 'app' });
// /admin/insights, the insights studio (docs/PLAN-insights-studio-20261016.md S2): born in the new design, Prest0 Inc.
// only. Today's design has no studio: InsightsStudioUnavailable is the minimal page shown if its chunks cannot load.
const InsightsStudioUnavailable = deferredRoute(() => import('./pages/InsightsStudioUnavailable'), '/admin/insights');
const InsightsStudioInTheDesign = withPavilion(InsightsStudioUnavailable, 'insights-studio', { chrome: 'app' });
// /admin/roadmap, the roadmap studio (docs/PLAN-roadmap-changelog-20261020.md R3): born in the new design, Prest0 Inc.
// only. Today's design has no studio: RoadmapStudioUnavailable is the minimal page shown if its chunks cannot load.
const RoadmapStudioUnavailable = deferredRoute(() => import('./pages/RoadmapStudioUnavailable'), '/admin/roadmap');
const RoadmapStudioInTheDesign = withPavilion(RoadmapStudioUnavailable, 'roadmap-studio', { chrome: 'app' });
// /activity/engineering, Prest0 Inc.'s engineering page (docs/PLAN-engineering-page-20261023.md): born in the new design,
// Prest0 Inc. only. Today's design has no such page: EngineeringUnavailable is the minimal page shown if its chunks cannot load.
const EngineeringUnavailable = deferredRoute(() => import('./pages/EngineeringUnavailable'), '/activity/engineering');
const EngineeringInTheDesign = withPavilion(EngineeringUnavailable, 'engineering', { chrome: 'app' });
// The referral program (docs/PLAN-referral-program-20261019.md): prest0.ai/referrals, the invitation every referral link
// opens (/referrals/join/CODE and /r/CODE), the person's own referral page (/referrals/dashboard) and the program terms
// on their own page (/referral-terms). Born in the new design; ReferralsUnavailable is the minimal page shown if their
// chunks cannot load (it still keeps an invitation's code, and still shows the terms).
const ReferralsUnavailable = deferredRoute(() => import('./pages/ReferralsUnavailable'), '/referrals');
const ReferralsInTheDesign = withPavilion(ReferralsUnavailable, 'referrals');
const ReferralInviteInTheDesign = withPavilion(ReferralsUnavailable, 'referral-invite');
const ReferralDashboardInTheDesign = withPavilion(ReferralsUnavailable, 'referral-dashboard', { chrome: 'app' });
const ReferralTermsInTheDesign = withPavilion(ReferralsUnavailable, 'referral-terms');
// /organization and /portal-controls (PV11 part 1, 2026-10-10): signed-in pages a first-time visitor never lands on.
// Their behaviour moved into useOrganizationPage and usePortalControlsPage; like /settings and /profile they load when
// their route opens, each its own file. scripts/check-chat-first-paint.mjs R4 holds them, their hooks and models (and
// the portal's client) off the first download: the `organization` and `portal-controls` rows.
const Organization = deferredRoute(() => import(/* webpackChunkName: "organization" */ './pages/Organization'), '/organization');
const PortalControls = deferredRoute(() => import(/* webpackChunkName: "portal-controls" */ './pages/PortalControls'), '/portal-controls');
// The Skills page (skills PR J, 2026-10-03): its own `skills` chunk, loaded when /settings/skills opens.
// Its calls load Firebase only when they run (src/services/skills/skillsPageClient.ts).
const Skills = deferredRoute(() => import(/* webpackChunkName: "skills" */ './components/Skills/SkillsPage'), '/settings/skills');
// The Personas page (assets everywhere AE13a, 2026-10-27): its own `personas` chunk, loaded when /settings/personas
// opens. Its calls load Firebase only when they run (src/services/personas/personasPageClient.ts).
const Personas = deferredRoute(() => import(/* webpackChunkName: "personas" */ './components/Personas/PersonasPage'), '/settings/personas');
// /settings/team (Team & Roles part 1, 2026-10-11): a signed-in page a first-time visitor never lands on. Its behaviour
// moved into its hooks (src/hooks/teamRoles/); like /settings and /profile it loads when its route opens, its own file.
// scripts/check-chat-first-paint.mjs R4 holds the page's own parts off the first download (the `team-roles` row); the
// invite dialog and the role editor are not in the row: the chat opens them too (GlobalChatSystem imports the dialog).
const TeamRolesPage = deferredRoute(() => import(/* webpackChunkName: "team-roles" */ './components/TeamRoles/TeamRolesPage'), '/settings/team');
// /settings/team in the new design (PV11, Team & Roles part 2): it stands under the app header, and its rebuild draws
// from the same hooks as today's page.
const TeamRolesInTheDesign = withPavilion(TeamRolesPage, 'team-roles', { chrome: 'app' });
// /integrations and its OAuth landing, /integrations/callback (Integrations part 1, PV13, 2026-10-11): a signed-in page a
// first-time visitor never lands on. Its behaviour moved into its hooks (src/hooks/integrations/); like /settings/team
// it loads when its route opens, its own file. The callback route below still decides at once, from the popup's launch
// mark, whether to skip the sign-in guard; the landing reads the mark when it mounts. scripts/check-chat-first-paint.mjs
// R4 holds the page's own parts and hooks off the first download (the `integrations` row); the setup form, the inbox
// question and their frame are not in the row: the app-wide hosts draw them for the chat.
const Integrations = deferredRoute(() => import(/* webpackChunkName: "integrations" */ './pages/Integrations'), '/integrations');
// The 3D model page (/preview/3d, today's design): it draws through the file preview's own hooks, which ride their own
// chunk (`file-preview`, docs/PLAN-prest0n-edit-20261011.md C1, 2026-10-18), so the page loads when it opens.
const Preview3DPage = deferredRoute(() => import(/* webpackChunkName: "preview-3d-page" */ './components/FilePreview/Preview3DPage'), '/preview/3d');
// /integrations and /integrations/callback in the new design (PV13, Integrations part 2): they stand under the app header,
// and the rebuild draws from the same hooks as today's page. The callback route's guard below draws this one declaration.
const IntegrationsInTheDesign = withPavilion(Integrations, 'integrations', { chrome: 'app' });
// /infrastructure and the fleet dashboard, /admin/vms (PV16 part 1, 2026-10-17): signed-in pages a first-time visitor
// never lands on. Their behaviour moved into their hooks (src/hooks/vm/); like /organization they load when their route
// opens, each its own file. scripts/check-chat-first-paint.mjs R4 holds them, their hooks and models off the first
// download (the `infrastructure` and `admin-vms` rows).
const Infrastructure = deferredRoute(() => import(/* webpackChunkName: "infrastructure" */ './pages/Infrastructure'), '/infrastructure');
const AdminVMDashboard = deferredRoute(() => import(/* webpackChunkName: "admin-vms" */ './pages/AdminVMDashboard'), '/admin/vms');
// /infrastructure and /admin/vms in the new design (PV16 part 2): they stand under the app header, and each rebuild draws
// from the same hook as today's page; /admin/vms's door stands inside the route's developer guard.
const InfrastructureInTheDesign = withPavilion(Infrastructure, 'infrastructure', { chrome: 'app' });
const AdminVmsInTheDesign = withPavilion(AdminVMDashboard, 'admin-vms', { chrome: 'app' });

// All lazy route imports below use `lazyWithRetry` (not bare `React.lazy`)
// so that a transient ChunkLoadError on a flaky network or a rolling deploy
// is retried 3× (same URL — retries only ride out transient blips) and, if
// still failing, handed to staleTabSelfHeal's manifest-gated recovery
// reload before any user-visible failure UI fires. Strictly orthogonal to
// the staged
// ResilientSuspenseFallback fix in fix/splash-overlay-overtrigger-2026-05-10
// — this hardens the rare *real* chunk-failure case so users get
// auto-recovery before the 30 s terminal-fallback budget elapses.
// NOTE: TeamRolesPage is NOT lazy here: since 2026-10-11 it loads through
// deferredRoute (above), which mounts no Suspense boundary.
const AcceptInvitePage = lazyWithRetry(() => import(/* webpackChunkName: "accept-invite" */ './pages/AcceptInvitePage'));
const AuthActionPage = lazyWithRetry(() => import(/* webpackChunkName: "auth-action" */ './pages/AuthActionPage'));

// In-app private routes below are synchronous because the React 18 route-level Suspense retry stall was recurring on account pages.
// Only low-traffic edge routes remain lazy.

const AuthCallback = lazyWithRetry(() => import(/* webpackChunkName: "auth-callback", webpackPrefetch: true */ './pages/AuthCallback'));
const AuthPage = lazyWithRetry(() => import(/* webpackChunkName: "auth", webpackPrefetch: true */ './pages/AuthPage'));
// PRE-72: Public SMS-consent signup surface (separate from /start trial flow).
const SmsTerms = lazyWithRetry(() => import(/* webpackChunkName: "sms-terms" */ './pages/SmsTerms'));
// The new design's door for /sms-terms (PV5a): today's lazy page above is its fallback, and what prest0.ai shows until
// the page's word goes live. SmsTerms keeps its name: tests-prod/core/staleChunkTarget.cjs follows a route to a
// lazyWithRetry page by name, and its own test aims /auth at SmsTerms as "another lazy page".
const SmsTermsDoor = withPavilion(SmsTerms, 'sms-terms');
// Header: see SYNCHRONOUS import block at top of file (with LandingPage / EnsureFixedAssets).

// Live-bundle verification marker for fix/unauthed-landing-page-resilient.
// Grep prod main.*.js for this string to confirm the deploy actually
// contains the fix. (LandingPage and EnsureFixedAssets are imported at
// the top of this file as synchronous imports — see the comment block
// next to those imports for the full bug write-up.)
//
// We attach the marker to `window` at module-eval time so the literal
// string survives terser tree-shaking. Without this side effect, an
// unused exported constant would be dead-code-eliminated and the
// marker would never appear in the production bundle.
export const __PREST0_UNAUTHED_LANDING_RESILIENT_MARKER__ =
  '__PREST0_UNAUTHED_LANDING_RESILIENT_2026_05_09__';
try {
  (window as any).__PREST0_UNAUTHED_LANDING_RESILIENT_MARKER__ =
    __PREST0_UNAUTHED_LANDING_RESILIENT_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/un-lazy-header.
export const __PREST0_UNLAZY_HEADER_MARKER__ =
  '__PREST0_UNLAZY_HEADER_2026_05_09__';
try {
  (window as any).__PREST0_UNLAZY_HEADER_MARKER__ =
    __PREST0_UNLAZY_HEADER_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/un-lazy-public-routes.
export const __PREST0_UNLAZY_PUBLIC_ROUTES_MARKER__ =
  '__PREST0_UNLAZY_PUBLIC_ROUTES_2026_05_09__';
try {
  (window as any).__PREST0_UNLAZY_PUBLIC_ROUTES_MARKER__ =
    __PREST0_UNLAZY_PUBLIC_ROUTES_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Apply safe animation HOC to components with known animation issues
// (LandingPage gets it inside LandingPageDoor, once its chunk has arrived.)
/**
 * The new design's home page: in the demonstration tree (prest0.ai/prest0design/), and on prest0.ai
 * itself while config/site-design.json lists `home` as live (since 2026-09-24). Fixed for the life of
 * the page: a page never changes address without reloading.
 */
const SHOWS_NEW_HOME = designShows('home');

/**
 * The app header in the new design (PV5-8:D): in the demonstration tree, and on prest0.ai once config/site-design.json
 * lists `app` as live. Fixed for the life of the page, as the home page's is.
 */
const SHOWS_NEW_APP_HEADER = designShows('app');

/**
 * The global header, unless a page of the new design has taken it over (src/design/pavilion/appChrome.ts): the
 * pavilion door claims it while loading or showing its page, which brings its own header, and releases it when it
 * falls back to today's page. A leaf, so a claim re-renders only this. Where the `app` surface shows, it is the new
 * design's header (withPavilion.tsx's door), with today's as its fallback.
 */
const AppHeader: React.FC = () => {
  if (useAppChromeClaimed('header')) return null;
  return SHOWS_NEW_APP_HEADER ? <PavilionAppHeaderDoor fallback={<Header />} /> : <Header />;
};

// Today's versions of the live public pages, each its rebuild's fallback, loaded on demand (TodaysPageDoor.tsx).
const TeamExpertiseToday = todaysPage('TeamExpertise');
const MissionToday = todaysPage('Mission');
const AboutToday = todaysPage('About');
const ForEnterpriseToday = todaysPage('ForEnterprise');
const ForLawyersToday = todaysPage('ForLawyers');
const ForIndividualsToday = todaysPage('ForIndividuals');
const PricingToday = todaysPage('Pricing');
const SignupToday = todaysPage('Signup');
const StartTrialToday = todaysPage('StartTrial');
const SafeTeamExpertise = withSafeAnimation(withPavilion(TeamExpertiseToday, 'team'));
const SafeMission = withSafeAnimation(withPavilion(MissionToday, 'mission'));
const SafeInsights = withSafeAnimation(withPavilion(InsightsUnavailable, 'insights'));
const SafeRoadmap = withSafeAnimation(withPavilion(RoadmapUnavailable, 'roadmap'));
const SafeChangelog = withSafeAnimation(withPavilion(RoadmapUnavailable, 'changelog'));
const SafePrivacy = withSafeAnimation(withPavilion(Privacy, 'privacy-policy'));
const SafeTerms = withSafeAnimation(withPavilion(Terms, 'terms-of-service'));
const SafeCookie = withSafeAnimation(withPavilion(Cookie, 'cookie-policy'));
const SafeAbout = withSafeAnimation(withPavilion(AboutToday, 'about'));
const SafeLegal = withSafeAnimation(withPavilion(Legal, 'legal'));
const SafeTrust = withSafeAnimation(withPavilion(Trust, 'trust'));
const SecurityToday = todaysPage('Security');
const SafeSecurity = withSafeAnimation(withPavilion(SecurityToday, 'security'));
const SafeBookDemo = withSafeAnimation(withPavilion(BookDemo, 'demo'));
// The sign-in page's rebuild in the new design (PV5-8:B): shown in the demonstration tree until its word goes live;
// today's page is its fallback, and its behaviour is the shared hooks both read (useAuthEntry, useSignInForm).
const SafeAuthPage = withSafeAnimation(withPavilion(AuthPage, 'auth'));
// The email-link page and the Google return, the same way (PV5-8:B): the route lines render these, never the pages.
const AuthActionInTheDesign = withPavilion(AuthActionPage, 'auth-action');
// /desktop stands under the app header (its registry row says chrome 'app'): the door claims no header for it.
const DesktopInTheDesign = withPavilion(AgentActivity, 'desktop', { chrome: 'app' });
// /wallet stands under the app header too (PV10): its rebuild draws from the same hooks as today's page.
const WalletInTheDesign = withPavilion(Wallet, 'wallet', { chrome: 'app' });
// /subscription stands under the app header too (PV9): its rebuild draws from the same hook as today's page.
const SubscriptionInTheDesign = withPavilion(Subscription, 'subscription', { chrome: 'app' });
// /settings stands under the app header too (PV5-8:B): its rebuild draws from the same hooks as today's page.
const SettingsInTheDesign = withPavilion(Settings, 'settings', { chrome: 'app' });
// /profile stands under the app header too (PV5-8:B): its rebuild draws from the same hook as today's page.
const ProfileInTheDesign = withPavilion(Profile, 'profile', { chrome: 'app' });
// /organization stands under the app header too (PV11): its rebuild draws from the same hook as today's page.
const OrganizationInTheDesign = withPavilion(Organization, 'organization', { chrome: 'app' });
// /portal-controls stands under the app header too (PV11): its rebuild draws from the same hook as today's page.
const PortalControlsInTheDesign = withPavilion(PortalControls, 'portal-controls', { chrome: 'app' });
// The service agreement (2026-10-11, PR C): born in the new design; today's design shows a page pointing to /subscription.
const AgreementInTheDesign = withPavilion(AgreementUnavailable, 'agreement');
// The trial's one screen (2026-10-20): born in the new design; today's design shows a page pointing to /subscription.
const TrialStartInTheDesign = withPavilion(TrialStartUnavailable, 'trial-start');
// The finish-setup screen (sign-in requirements, 2026-10-29): born in the new design, outside the app chrome; today's
// design shows a page that reloads or signs out.
const SignInSetupInTheDesign = withPavilion(SignInSetupUnavailable, 'sign-in-setup');
// The open ROI reports, the ROI calculator and the solutions pages (GSD revamp, 2026-10-15): born in the new design;
// today's design shows GsdPageUnavailable at their addresses.
const RoiInTheDesign = withPavilion(GsdPageUnavailable, 'roi');
const RoiCalculatorInTheDesign = withPavilion(GsdPageUnavailable, 'roi-calculator');
// The savings and time calculators (per-employee pricing, 2026-10-30): born in the new design too.
const RoiSavingsInTheDesign = withPavilion(GsdPageUnavailable, 'roi-savings');
const SolutionsInTheDesign = withPavilion(GsdPageUnavailable, 'solutions');
// /settings/skills stands under the app header too (PV11): its rebuild draws from the same hook as today's page.
const SkillsInTheDesign = withPavilion(Skills, 'skills', { chrome: 'app' });
// /settings/personas stands under the app header too (AE13a): its rebuild draws from the same hook as today's page.
const PersonasInTheDesign = withPavilion(Personas, 'personas', { chrome: 'app' });
// /people, a person's contacts book (assets everywhere AE14a, 2026-10-27): born in the new design, under the app header.
// Today's design has no such page: PeopleUnavailable is what it shows there, and the minimal page if its chunks cannot load.
const PeopleUnavailable = deferredRoute(() => import('./pages/PeopleUnavailable'), '/people');
const PeopleInTheDesign = withPavilion(PeopleUnavailable, 'people', { chrome: 'app' });
// /preview/3d stands under the app header too (PV16): its rebuild draws from the same hook as today's page, which
// covers the whole screen and is the fallback.
const Preview3DInTheDesign = withPavilion(Preview3DPage, 'preview-3d', { chrome: 'app' });
const AuthCallbackInTheDesign = withPavilion(AuthCallback, 'auth-callback');
// The invitation page, the same way (PV5-8:B).
const AcceptInviteInTheDesign = withPavilion(AcceptInvitePage, 'accept-invite');
// The 404, the catch-all route's page, the same way (PV5-8:A): the rebuild shows in the demonstration tree until its
// word goes live; today's page is its fallback, and both say notFoundWords.ts.
const NotFoundInTheDesign = withPavilion(NotFound, 'not-found');
// The moonshot demo, the same way (PV5-8:A): its rebuild stands under the app header, as today's page does, and shows in
// the demonstration tree until its word goes live; today's page, the door's deferred route (./moonshot/MoonshotDoor), is
// its fallback, and both draw from useMoonshotChat and moonshotWords inside the demo's one provider.
const MoonshotInTheDesign = withPavilion(MoonshotPage, 'moonshot', { chrome: 'app' });
// The public signup page, the same way (PV5-8:B).
const SignupInTheDesign = withPavilion(SignupToday, 'signup');
const StartTrialInTheDesign = withPavilion(StartTrialToday, 'start');
const SafeForEnterprise = withSafeAnimation(withPavilion(ForEnterpriseToday, 'for-enterprise'));
const SafeForLawyers = withSafeAnimation(withPavilion(ForLawyersToday, 'for-lawyers'));
const SafeForIndividuals = withSafeAnimation(withPavilion(ForIndividualsToday, 'for-individuals'));
// The pricing page's rebuild in the new design (PV5-8:B): shown in the demonstration tree until its word goes live;
// today's page is the fallback. Not wrapped in withSafeAnimation, as the route never was.
const SafePricing = withPavilion(PricingToday, 'pricing');

// Live-bundle verification marker for un-lazying authenticated/private routes.
// This removes the recurrent route-level Suspense stall on account pages
// such as /desktop, /wallet, /integrations, /onboarding, and /subscription.
export const __PREST0_UNLAZY_PRIVATE_ROUTES_MARKER__ =
  '__PREST0_UNLAZY_PRIVATE_ROUTES_2026_05_12__';
try {
  (window as any).__PREST0_UNLAZY_PRIVATE_ROUTES_MARKER__ =
    __PREST0_UNLAZY_PRIVATE_ROUTES_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for un-lazying /settings/team (the last
// authenticated account page still lazy-loaded). Removes the recurrent
// route-level Suspense stall that surfaced the "this page is taking longer
// than expected to load. refresh" UI on the team & roles settings page.
// Kept since the page left the first download (2026-10-11): it loads through
// deferredRoute, which is not React.lazy and mounts no Suspense boundary, so
// the stall whose fix this marker verified cannot come back.
export const __PREST0_UNLAZY_TEAM_SETTINGS_MARKER__ =
  '__PREST0_UNLAZY_TEAM_SETTINGS_2026_07_08__';
try {
  (window as any).__PREST0_UNLAZY_TEAM_SETTINGS_MARKER__ =
    __PREST0_UNLAZY_TEAM_SETTINGS_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/splash-still-loading-stuck-FH-QA.
// Confirms the FH-QA-2026-05-21 fix that always removes the orphaned
// `#app-splash-still-loading` subtitle from the splash watchdog the
// moment React mounts. Grep prod main.*.js for this string to verify
// the fix shipped.
export const __PREST0_SPLASH_STILL_LOADING_CLEANUP_MARKER__ =
  '__PREST0_SPLASH_STILL_LOADING_CLEANUP_2026_05_21__';
try {
  (window as any).__PREST0_SPLASH_STILL_LOADING_CLEANUP_MARKER__ =
    __PREST0_SPLASH_STILL_LOADING_CLEANUP_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Docked-chat page reflow (owner directive 2026-08-24 — the app-wide
// re-center the chatModeSignal hook was prepared for). While the chat pane
// is OPEN as the docked right side panel, routed page content lays out in
// the remaining space on the left — the same reflow a narrower browser
// window produces, so centered pages give up their side white space first.
// The header, banner band and overlay hosts deliberately stay full-width.
// Keyed on the <html> attribute the one persistent chat mount stamps (see
// chatModeSignal.ts), so this shell needs no chat state of its own.
// Below DOCKED_COMPANION_MIN_VIEWPORT_PX (821px, the first width that is not
// a phone) the chat is the phone's full-screen takeover and the squeeze stands
// down. Above it the page keeps 400px or more beside the rail at any width the
// person drags the chat to (dockedWidth.ts) — the footprint below reads that
// width through the variable this element holds itself (DockedContentShell).
const RoutedContentShell = styled.div`
  width: 100%;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  /* Same 0.4s as the pane's dock glide (ChatContainer right/width) so page
     and pane move as one; header's house ease keeps it platform-consistent. */
  transition: width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* The docked divider mid-drag (DockedDivider.tsx): the page tracks the
     pointer rather than gliding after it. */
  &[data-docked-resizing='true'] {
    transition: none;
  }

  @media (min-width: ${DOCKED_COMPANION_MIN_VIEWPORT_PX}px) {
    html[data-prest0-chat-mode='docked'] & {
      /* Pane width + its outer inset + the gutter beside it — the ONE rail
         footprint every companion surface reserves (dockedCompanion.ts), so
         page content, the preview panel and floating windows all line up. */
      width: calc(100% - ${DOCKED_RAIL_FOOTPRINT_CSS});
    }
  }
`;

/**
 * The routed page's shell, holding the docked chat's width (2026-10-08). The
 * variable is not inherited (dockedWidth.ts), so this element — the one whose
 * own width rule reads it — holds it itself. A commit re-renders only this
 * wrapper: `children` is the same element App built, so React does not walk
 * into the routes. During a drag the divider writes the same variable here
 * directly, once a frame, and nothing re-renders at all.
 */
const DockedContentShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dockedWidth = useDockedWidthChoice();
  return (
    <RoutedContentShell data-docked-content-shell="" style={dockedWidthStyle(dockedWidth)}>
      {children}
    </RoutedContentShell>
  );
};

// 2026-08-15 owner incident (second occurrence): this guard used to admit on
// `isAuthenticated OR hasFirebaseUser` — the left half is the localStorage-
// cached AuthContext identity, which a signed-out visitor can still carry
// (its backing token is a client-minted random string nothing expires), so
// stale cache ALONE opened every portal page. The Firebase probe was also
// one-shot, so a mid-session sign-out never re-evaluated. Now: Firebase's
// live confirmed state (useConfirmedAuth) is the ONLY authority, fail-closed,
// with the hydration gate preserved.
//
// THE SESSION GATE (sign-in requirements, 2026-10-29; docs/PLAN-signin-requirements-20261029.md). A session whose ID
// token carries the setup-only mark is refused by every rule and callable but the setup doors, so while it holds, every
// page this guards goes to /sign-in-setup (keeping where the person was headed) and the user context is not loaded:
// the finish-setup screen never sits on refusals. `setupScreen` is that one screen's own guard, which signs a
// signed-out visitor in like any other and never sends the screen to itself.
const ProtectedRoute = ({ children, setupScreen = false }: { children: React.ReactNode; setupScreen?: boolean }) => {
  const location = useLocation();
  const { checked, signedIn } = useConfirmedAuth();
  const setupHold = useSignInSetupHold();
  const setupReading = useSignInSetupReading();

  // Subscribe to auth events with strict data isolation
  React.useEffect(() => {
    // Initialize user data isolation manager on first load
    const initializeIsolation = async () => {
      try {
        const { userDataIsolation } = await import('./utils/userDataIsolation');
        userDataIsolation.initialize();
        console.log('✅ User data isolation initialized for protected route');
      } catch (error) {
        console.error('Failed to initialize user data isolation:', error);
      }
    };
    
    initializeIsolation();
    
    const unsub = subscribeToAuthState(async (user: { uid: string; isAnonymous: boolean } | null) => {
      if (user) {
        // A terms record that could not be sent waits in this browser; TermsUpdateHost sends it, on every page (SOC 2
        // Phase 1, 2026-10-13 — it used to go only from here, so never from / or the marketing pages).
        try {
          // A setup-only session reads nothing: its token is read first, and the context waits for a session without
          // the mark (bounded: an unreadable token is no hold, src/lib/signInSecurity/setupSession.ts).
          if (await whenSetupKnown(user.uid)) return;
          console.log('🔒 Loading isolated user context for UID:', user.uid);
          await loadUserContext(user.uid);
        } catch (error) {
          console.error('Failed to load user context:', error);
        }
      } else {
        console.log('🚫 No authenticated user in protected route');
      }
    });
    return () => { try { unsub(); } catch {} };
  }, []);

  // Moonshot demo mode bypasses the auth wall entirely so the scripted
  // journeys play back on any shareable URL without a login flow.
  if (isMoonshotMode()) {
    return <ErrorBoundary>{children}</ErrorBoundary>;
  }

  // Avoid redirect races by waiting until we finish checking Firebase.
  // Branded beat instead of blank frames (2026-08-09): entering /onboarding
  // or /desktop used to paint nothing while this check ran. It waits in the
  // light of the page it guards (D-92): never a noon beat before a dusk page.
  if (!checked) return <GuardLoading ahead={children} title="one moment…" />;

  // Firebase-confirmed sign-in is the ONLY admission — cached identities
  // never open a portal page. Pass current path as returnUrl for after login.
  // Navigate moves only after this commit, so the beat stays up until the
  // sign-in page takes over: the light holds through the move (D-92).
  if (!signedIn) {
    return (
      <>
        <GuardLoading ahead={children} title="one moment…" />
        <Navigate to="/auth" state={{ returnUrl: location.pathname + location.search }} />
      </>
    );
  }

  // The session's token is being read for the setup-only mark (bounded: src/lib/signInSecurity/setupSession.ts): the
  // page waits in its own light rather than opening and being moved away from.
  if (setupReading) return <GuardLoading ahead={children} title="one moment…" />;

  // The session must finish setting up its sign-in first: the one screen that does it, keeping the return address.
  if (setupHold && !setupScreen) {
    return (
      <>
        <GuardLoading ahead={children} title="one moment…" />
        <Navigate to="/sign-in-setup" replace state={{ returnUrl: location.pathname + location.search }} />
      </>
    );
  }

  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};

/**
 * What every page mounts around itself that reads the signed-in person's data — the header and its bell, the notice
 * band's sources, the gates, the chat engine's host and the hosts over any page — stands down while the session carries
 * the setup-only mark, so nothing sits on a refusal under the finish-setup screen. Read off the session's own token
 * (src/hooks/useSignInSetupSession.ts); tests/routing/signInSetupGate.test.tsx holds every global mount of AppContent
 * inside it.
 */
const StandsDownForSignInSetup = ({ children }: { children: React.ReactNode }) => (useSignInSetupHold() ? null : <>{children}</>);

// Simple access wall for permission-gated routes (matches the app's lowercase
// voice; no navigation side effects, so a wrong guess never bounces the user).
// `data-route-restricted` names the gate that refused: the live signed-in row
// skips such a page as not its account's to open (tests-prod/core/pavilionPages.cjs).
const RouteRestricted = ({ kind, message }: { kind: 'developer' | 'permission'; message: string }) => (
  <div data-route-restricted={kind} style={{ padding: '4rem 2rem', textAlign: 'center', fontFamily: 'inherit', color: '#555' }}>
    {message}
  </div>
);

/**
 * Developer-org route gate (catalog v4): wraps developer tooling routes
 * (/admin/vms). Renders ONLY when the server confirmed the active workspace is
 * a developer org (useDeveloperOrg, fail-closed) AND the caller holds the
 * required developer permission. This is the UI half of the two-key doctrine —
 * every data callable behind these routes re-checks the developer-org gate +
 * permission server-side, so this gate can only hide chrome, never grant.
 */
const DeveloperRoute = ({ permission, children }: { permission: string; children: React.ReactNode }) => {
  const { isDeveloperOrg, loading: devLoading } = useDeveloperOrg();
  const rbac = useRbac();
  if (devLoading || rbac.loading) return <GuardLoading ahead={children} title="one moment…" />;
  // FAIL CLOSED: developer routes never render on an error or a non-developer
  // workspace — including for that workspace's owner.
  if (!isDeveloperOrg || !rbac.hasPermission(permission)) {
    return <RouteRestricted kind="developer" message="this page is restricted to the prest0 platform engineering workspace." />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/**
 * Account-permission route gate: wraps account-governance routes
 * (/portal-controls). Fails OPEN on an RBAC load error — the page's write
 * endpoints are server-guarded (account.manage) regardless, so a transient
 * error must not lock a legitimate admin out of a read-only view.
 */
const PermissionRoute = ({ permission, children }: { permission: string; children: React.ReactNode }) => {
  const rbac = useRbac();
  if (rbac.loading) return <GuardLoading ahead={children} title="one moment…" />;
  if (!rbac.error && !rbac.hasPermission(permission)) {
    return <RouteRestricted kind="permission" message="you don't have access to this page. ask a workspace owner or admin." />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/**
 * /integrations/callback: the OAuth popup's landing bypasses ProtectedRoute
 * when the popup handshake mark is present — the minimal "connected — close
 * this window" card renders no user data and self-closes, and bouncing it
 * through auth guards left the popup stranded on whatever page the guard
 * redirected to (2026-08-14 owner repro, brand-new trial user). Full-window
 * OAuth returns (no mark) keep the guarded Integrations page.
 */
const IntegrationsCallbackRoute = () => {
  const [isPopup] = React.useState(() => {
    try {
      return !!readConnectPopupLaunch();
    } catch {
      return false;
    }
  });
  if (isPopup) return <IntegrationsInTheDesign />;
  return <ProtectedRoute><IntegrationsInTheDesign /></ProtectedRoute>;
};

// A chunk file that was not a script (a pruned chunk answered with index.html and
// run as one): its SyntaxError is an early duplicate of the ChunkLoadError that
// follows, and that rejection decides whether the failure is owned (below).
// Capture phase, so it is cancelled before any other listener reports it.
window.addEventListener('error', (event) => {
  handleChunkScriptErrorEvent(event);
}, true);

// Handle global errors from Promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.warn('Unhandled promise rejection:', event.reason);

  const errorMessage = event.reason?.message || '';

  // ChunkLoadError — chunk loading failures during navigation/streaming.
  // Suppressed ONLY while a loader has signaled it owns recovery (retryImport,
  // or noteChunkRecoveryActivity for a loader that never reloads, like the chat
  // skin's door): an unconditional preventDefault here used to swallow every
  // unhandled 'Failed to fetch' / 'Unexpected token' rejection app-wide
  // (webpack surfaces the HTML-as-JS case to the REJECTION as ChunkLoadError;
  // the SyntaxError goes to the window error event — those strings never
  // belonged in this branch). The rule lives in lazyWithRetry.ts so a test
  // drives it (handleChunkRejectionEvent).
  if (handleChunkRejectionEvent(event)) return;

  // Check if this is an animation-related error that we can safely ignore
  if (
    errorMessage.includes("Cannot use 'in' operator to search for 'animation'") ||
    errorMessage.includes("Cannot set properties of undefined")
  ) {
    // Prevent the error from bubbling up
    event.preventDefault();
    console.warn('Animation-related error suppressed:', errorMessage);
  }
  // Suppress noisy internal errors from Firebase callable/Firestore that are transient
  try {
    const code = String(event?.reason?.code || event?.reason?.status || '').toLowerCase();
    const message = String(event?.reason?.message || '').toLowerCase();
    if (code === 'internal' || message.includes('internal')) {
      event.preventDefault();
      console.warn('Suppressed transient Firebase internal error');
    }
  } catch {}
});

// Log Firestore permission-denied errors for security monitoring
window.addEventListener('unhandledrejection', async (event) => {
  try {
    const reason: any = event.reason || {};
    const code: string | undefined = reason?.code || reason?.status;
    if (code === 'permission-denied') {
      try { (await import('./lib/logging/secureLogger')).logRuleDenied(null, 'unknown', 'firestore', 'unhandledrejection'); } catch {}
    }
  } catch {}
});

// Add effect to run migration once when app initializes
function initializeMigration() {
  // Run account and member ID migration
  console.log('Initializing account and member ID migration...');
  import(/* webpackChunkName: "profile-migration" */ './utils/profileManager')
    .then(mod => {
      try {
        mod.migrateAllProfileIds();
      } catch (err) {
        console.warn('Profile migration failed:', err);
      }
    })
    .catch(err => {
      console.warn('Failed to load profile migration module:', err);
    });
}

// Defer migration to post-first-paint via effect below

const AppWithProviders: React.FC = () => {
  return (
    <AuthProvider>
      <OLVRateProvider>
        <WalletSummaryProvider>
          <AITransactionProvider>
            <ConversationHistoryProvider>
              {/* TopUpModalProvider sits inside WalletSummaryProvider + OLVRateProvider
                  so the in-place top-up modal has wallet + rate context, and above
                  AppContent so every surface (incl. GlobalChatSystem) can useTopUp(). */}
              <TopUpModalProvider>
                {/* FloatingPreviewProvider: app-level multitask preview windows
                    (2026-08-02 directive) — above AppContent so chat surfaces AND
                    routed pages share one floating layer that survives navigation. */}
                <FloatingPreviewProvider>
                  <AppContent />
                </FloatingPreviewProvider>
              </TopUpModalProvider>
            </ConversationHistoryProvider>
          </AITransactionProvider>
        </WalletSummaryProvider>
      </OLVRateProvider>
    </AuthProvider>
  );
};

/**
 * AppGlobalChat — thin component that renders the single app-level chat for
 * the current route, through ChatEngineLoader: the engine itself arrives a
 * moment after the page paints (PR-S, 2026-09-22). This wrapper stays eager —
 * its setSyncPaused effect must run at first paint.
 * Lives inside AppContent so it has access to Router context.
 */
const AppGlobalChat: React.FC = () => {
  const { shouldRender, config, pageContext } = usePageChatConfig();
  const tenantChatConfig = useTenantChatConfig();
  // Task #56 workspace lock: the chat IS the workspace, and it renders on
  // marketing surfaces (/, /pricing, …) that the route gate deliberately
  // leaves reachable — so a mid-onboarding account could still chat from
  // there. Hide (never unmount — see the persistent-mount note below) while
  // onboarding is owed; the hook is fail-open, so an error can't kill chat.
  const onboardingGate = useTrialOnboardingGate();

  // Pause the global Firestore conversation-history sync on any surface
  // that has the thread switcher enabled. The thread switcher owns its
  // own per-thread localStorage persistence; running both systems at
  // once causes cross-thread message bleed (thread-B's reply gets
  // flattened into thread-A because the global history listener force-
  // syncs the single-bucket doc back into React). When threads are off,
  // global sync resumes so cross-device single-thread history still
  // works.
  const maybeHistory = useMaybeConversationHistory();
  const threadsOn = config.enableThreadSwitcher === true;
  React.useEffect(() => {
    if (maybeHistory?.setSyncPaused) {
      maybeHistory.setSyncPaused(threadsOn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadsOn]);

  // P3 (persistent mount): NEVER unmount for route reasons — excluded routes
  // render the widget in hidden mode instead, so the instance (conversation,
  // drafts, in-flight streams, thread store) survives crossing /auth or
  // /onboarding. The unmount was never the auth boundary — account switches
  // purge via app:session_reset and the uid-scoped storage re-key.

  // Tenant welcome on EVERY surface (2026-07-13, chat-surface uniformity):
  // the account-personalized welcome (written during onboarding) used to
  // apply only on /desktop//agent-activity, so the same account saw a
  // different greeting base per page. It now wins wherever it's loaded;
  // signed-out visitors (tenant config never loads) keep the per-page
  // marketing welcome from the registry.
  // Memoized (2026-08-09 greeting parity): a fresh object every render used to
  // churn composeGreeting's identity downstream, re-firing the greet-on-focus
  // effect per render and letting each tab continually re-assert its own copy.
  // Fresh team member (owner directive 2026-08-12): for a few minutes after
  // an invitation is accepted, the first message is the JOIN greeting —
  // "you're now inside <org>'s workspace" — mirroring how a new organization
  // is greeted after its own onboarding. Wins over the tenant welcome (that
  // copy addresses the org owner, not someone who just walked in the door).
  // Memoized per route-config change: a fresh object every render would
  // churn composeGreeting downstream (see the mergedConfig note below).
  const justJoined = React.useMemo(() => getJustJoinedOrg(), [config]);

  const mergedConfig = React.useMemo(() => ({
    ...((tenantChatConfig.loaded && tenantChatConfig.welcomeMessage)
      ? { ...config, welcomeMessage: { text: tenantChatConfig.welcomeMessage } }
      : config),
    // Page-declared welcome + starters (2026-08-12): a route that carries its
    // OWN welcome copy in the page context registry — today only
    // /accept-invite's invitation greeting — wins over the guest/tenant
    // fallbacks, which would otherwise always load truthy values and bury
    // it. Ordered after the tenant spread so the page copy prevails, and
    // before the just-joined overlay so that (fresher, member-specific)
    // greeting still tops everything.
    ...(config.welcomeMessage ? { welcomeMessage: config.welcomeMessage } : {}),
    ...(justJoined?.firmName
      ? {
          welcomeMessage: {
            text:
              `welcome — you're now inside ${justJoined.firmName}'s workspace. ` +
              `I'm Prest0n, your team's AI employee: I can research, draft, and handle ` +
              `day-to-day work. ask me anything to get started.`,
          },
        }
      : {}),
    // F4-A (2026-07-13): canonical one-click starter chips for the empty-thread
    // welcome. Flow on every surface once tenant config loads, independent of
    // whether the welcome *message* text was overridden (recommendation 4a).
    ...(tenantChatConfig.loaded && tenantChatConfig.welcomeStarters?.length
      ? { welcomeStarters: tenantChatConfig.welcomeStarters }
      : {}),
    // Page-declared starter chips must survive the tenant/guest spread above
    // (review 2026-08-13: ordered earlier, the invitation chips were dead —
    // every visitor got guest/account starters under the invitation
    // greeting). Only /accept-invite declares page starters today.
    ...(config.welcomeStarters?.length ? { welcomeStarters: config.welcomeStarters } : {}),
    // Memory-distilled net-new greeting suggestions (2026-07-13): flow the
    // account's suggestions into the greeting composer on every surface —
    // along with the distillation's token usage, so the greeting bubble's
    // mandatory ⓘ can attribute the AI compute that produced them.
    ...(tenantChatConfig.loaded && tenantChatConfig.suggestedAutomations?.length
      ? {
          suggestedTasks: tenantChatConfig.suggestedAutomations,
          suggestedTasksUsage:
            (tenantChatConfig.suggestedAutomationsUsage as TokenUsageSnapshot | undefined) ?? null,
        }
      : {}),
    // Multi-tab greeting parity (2026-08-09): the composer must never mint a
    // greeting from an UNSETTLED config, and every composed greeting carries
    // a rank derived from where its copy came from (see greetingRank.ts).
    welcomeReady: tenantChatConfig.loaded,
    welcomeSource: tenantChatConfig.welcomeSource,
    hidden: !shouldRender || (!onboardingGate.loading && onboardingGate.needsOnboarding),
  }), [config, tenantChatConfig, shouldRender, justJoined, onboardingGate]);

  // `?chat=expanded` (tri_mvfr3ud2_6eea3bbc90, 2026-10-20): wherever the chat shows, on arrival or after a sign-in.
  useChatExpandedAddress(!mergedConfig.hidden);

  return <ChatEngineLoader config={mergedConfig} />;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  // React Router v6 matches routes case-insensitively ('/Pricing' renders the
  // pricing page), so every layout decision must compare on the lowercase
  // path — exact-compares against location.pathname let capitalized URLs
  // render the wrong header/footer chrome (owner screenshot 2026-08-02).
  const routePath = location.pathname.toLowerCase();
  const isLandingPage = routePath === '/';

  const isTeamPage = routePath === '/team' || routePath === '/about/team/team-expertise';
  const isMissionPage = routePath === '/about/mission/project-mission' || routePath === '/mission';
  const isInsightsPage = routePath.startsWith('/insights');
  const isPrivacyPage = routePath === '/privacy-policy' || routePath === '/legal/privacy-policy';
  const isTermsPage = routePath === '/terms-of-service' || routePath === '/legal/terms-of-service';
  const isCookiePage = routePath === '/cookie-policy' || routePath === '/legal/cookie-policy';
  const isPricingPage = routePath === '/pricing';
  const isStartTrialPage = routePath === '/start' || routePath === '/trial';
  // PRE-72: /signup + /sms-terms are public pages with their own layouts.
  const isSignupPage = routePath === '/signup';
  const isSmsTermsPage = routePath === '/sms-terms';
  const isAuthPage = routePath === '/auth';
  // Auth email action handler (verify/reset, task #50): a focused, chrome-
  // free page like /auth itself — no header nav to wander off through mid-
  // reset (owner directive 2026-08-12).
  const isAuthActionPage = routePath === '/auth/action';
  // Invitation landing (owner directive 2026-08-12): a distraction-free
  // funnel. The page renders its own non-navigating brand mark top-left; the
  // global header's nav icons and login button must not compete with the
  // accept decision. The Prest0n chat stays (invite-aware via the page
  // context registry) — only the header chrome goes.
  const isAcceptInvitePage = routePath === '/accept-invite';
  const isProfilePage = routePath === '/profile';
  const isSettingsPage = routePath === '/settings';
  const isSecurityPage = routePath.startsWith('/security');
  const isWalletPage = routePath === '/wallet';
  const isNotificationsPage = routePath === '/notifications';
  // Onboarding is a focused, full-screen flow: no global header (the page
  // renders its own minimal top bar) so there are no mid-setup escape hatches.
  const isOnboardingPage = routePath === '/onboarding';
  // The finish-setup screen (sign-in requirements, 2026-10-29) stands outside the app chrome, in the new design's
  // focused frame: nothing on it may lead anywhere a setup-only session is refused.
  const isSignInSetupPage = routePath === '/sign-in-setup';
  const isPrivateRoute = isAuthPage || isProfilePage || isSettingsPage;
  const shouldShowHeader =
    !isLandingPage &&
    !isTeamPage &&
    !isMissionPage &&
    !isInsightsPage &&
    !isPrivacyPage &&
    !isTermsPage &&
    !isCookiePage &&
    !isPricingPage &&
    !isStartTrialPage &&
    !isSignupPage &&
    !isSmsTermsPage &&
    !isAuthPage &&
    !isAuthActionPage &&
    !isAcceptInvitePage &&
    !isSecurityPage &&
    !isOnboardingPage &&
    !isSignInSetupPage;
  // Log current route for debugging
  React.useEffect(() => {
    console.log('Current location:', location);
    console.log('Current route hash:', window.location.hash);
    console.log('Current route pathname:', window.location.pathname);
  }, [location]);

  // Reliability: Auto‑flush debounced writes on route changes
  React.useEffect(() => {
    try { flushAllPendingUpdates(); } catch {}
  }, [location.pathname]);

  // Install global auto-flush listeners once
  React.useEffect(() => {
    try { installDebounceAutoFlush(); } catch {}
  }, []);

  // Push notifications are no longer registered unconditionally on mount —
  // the permission is requested from a user's click (the notifications
  // surface owns that flow), never fired at every page load.

  // Stale-tab self-heal watcher (returning-tab reliability, 2026-09-01): on
  // wake it probes whether the RUNNING bundle is provably at risk (pruned
  // assets / retention-coupled boot age) and silently reloads behind
  // staleTabSelfHeal's guards. Renders nothing and registers no banner — a
  // mere update stays the banner's click (2026-08-20 doctrine).
  React.useEffect(() => initStaleTabSelfHeal(), []);

  // Connect-funnel sink (plan P0 #8, 2026-09-02): the ONE production
  // consumer of logConnectFunnel — batches every funnel record to the
  // server day-row (ops/connectFunnel). Installed once; never load-bearing.
  React.useEffect(() => {
    try {
      return installConnectFunnelAppender();
    } catch {
      return undefined;
    }
  }, []);

  // App-level loading watchdog (added for fix/listen-channel-block).
  //
  // Why: a browser extension blocking the Firestore long-poll
  // (`firestore.googleapis.com/.../Listen/channel` -> ERR_BLOCKED_BY_CLIENT)
  // can leave downstream code paths waiting on a real-time subscription
  // even after one-shot hydration completes. We need diagnostic visibility
  // when this happens.
  //
  // IMPORTANT: this watchdog is SILENT / LOG-ONLY. It does NOT render any
  // user-visible failure UI. The 4 s budget is correct for a diagnostic
  // log because false-positives in DevTools / monitoring are cheap; what
  // is NOT cheap is rendering a "refresh" failure UI on healthy-but-slow
  // loads — that's the bug we fixed in
  // fix/splash-overlay-overtrigger-2026-05-10 by moving the
  // user-visible failure UI in `ResilientSuspenseFallback` from a 4 s
  // hard timer to a staged 6 s "still loading…" + 30 s terminal-refresh
  // budget (see src/components/ResilientSuspenseFallback.tsx and
  // src/utils/loadingGate.ts).
  //
  // The bundle-state-aware splash watchdog in `public/index.html` handles
  // the case where React itself never paints (chunk 404 / module-eval
  // error). This watchdog is the belt-and-suspenders for *post-paint*
  // gates: if the document is ready but the route still hasn't
  // committed visible content within 4 s, we log a high-visibility
  // warning so the issue is obvious in DevTools and so production
  // monitoring picks it up.
  React.useEffect(() => {
    let fired = false;
    const handle = window.setTimeout(() => {
      if (fired) return;
      try {
        const root = document.getElementById('root');
        const splash = document.getElementById('app-splash');
        const splashStillVisible = !!splash && !splash.classList.contains('hidden');
        const rootEmpty = !root || root.children.length === 0;

        // Detect stuck "loading…" / "loading..." surfaces anywhere in the
        // page so we can warn even when a downstream gate (not the App
        // itself) is the culprit.
        const visibleText = (document.body?.innerText || '').toLowerCase();
        const stuckOnLoading =
          visibleText.length < 200 && /(^|\s)loading[.\u2026]/.test(visibleText);

        if (rootEmpty || splashStillVisible || stuckOnLoading) {
          // eslint-disable-next-line no-console
          console.warn(
            '[loadingGate:app-watchdog] App still appears to be loading after ' +
              `${LOADING_GATE_DEFAULT_TIMEOUT_MS}ms. rootEmpty=${rootEmpty} ` +
              `splashStillVisible=${splashStillVisible} stuckOnLoading=${stuckOnLoading}. ` +
              'Common cause: a Firestore real-time subscription was blocked ' +
              '(net::ERR_BLOCKED_BY_CLIENT from a browser extension blocking ' +
              'firestore.googleapis.com/.../Listen/channel). Hydration is the source ' +
              'of truth for first paint; live subscription is best-effort only. ' +
              'Set localStorage.DEBUG_LOADING_GATE="1" and reload to see per-gate logs.',
          );
        }
      } catch {
        // Watchdog must never throw.
      }
      fired = true;
    }, LOADING_GATE_DEFAULT_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, []);

  // Render-time loading-gate telemetry (cheap when DEBUG_LOADING_GATE is off).
  logLoadingGate('app-content', true, '<none>');

  // Custom error handler for React components
  const handleComponentError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Component error:', error, errorInfo);
  };

  // Defer heavy migrations until after first paint/idle time
  React.useEffect(() => {
    const scheduleIdle = (task: () => void) => {
      const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => void);
      if (typeof ric === 'function') {
        ric(task, { timeout: 2000 });
      } else {
        setTimeout(task, 0);
      }
    };
    scheduleIdle(initializeMigration);
  }, []);

  return (
    <ErrorBoundary onError={handleComponentError}>
      <AppContainer>
        {/* Add noindex for auth and private routes (in the tree the preview's own noindex is already the one) */}
        {headWritesNoindex(isPrivateRoute) && (
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        )}
        <ScrollToTop />
        {/* A session that must finish setting up its sign-in reads nothing: everything from here to the routes, and
            everything after them, stands down until it has (StandsDownForSignInSetup, the session gate). */}
        <StandsDownForSignInSetup>
        <Suspense fallback={null}>
          {shouldShowHeader && <AppHeader />}
        </Suspense>
        {/* THE one status strip (owner directive 2026-08-14): the center
            renders a single band in normal flow directly below the header
            (pushes content down; never overlays the header) and ROTATES when
            more than one notice is eligible. The wrapped sources keep 100% of
            their eligibility/suppression logic but register with the center
            instead of stacking their own strips. ORG-USAGE-2026's guarantee
            stands: nobody gets paused without seeing it coming. */}
        <GlobalBannerCenter>
          {/* The public demo (prest0.ai/demo/<industry>, exception M1): that this is the live demo with a made-up firm,
              and the way out to the real site. Only on a demo page; elsewhere it draws and loads nothing. */}
          <DemoBandSource />
          {/* No grace (2026-10-11): a plan payment that didn't go through
              pauses work — owners and billing managers see it on every page. */}
          <BillingPausedBanner />
          <GlobalLowBalanceBanner />
          <TrialStatusBanner />
          <EmailVerifyBanner />
          {/* Sign-in requirements: a workspace's two-step requirement counted down to its date, louder as it comes
              (the email requirement rides the email notice above). */}
          <SignInRequirementBanner />
          <PhoneVerifyBanner />
          {/* F-16 (Prest0n Everywhere S1c-1b-2): a number a YES reply confirmed is told once to confirm it
              with a code, before a YES stops counting. */}
          <ProvePhoneBanner />
          <UsageLimitBanner />
          {/* §7.10 pause freeze (owner decision 5): a suspended member always
              knows the workspace is read-only, never a silent wall. */}
          <PausedMembershipBanner />
          {/* Idle-lifecycle strip (2026-08-18): "goes to sleep soon" with a
              one-click keep-it-on, asleep with a one-click wake, and waking
              progress. Billing sleeps stay with the dunning banners. */}
          <VmLifecycleBanner />
          {/* Deploy freshness (2026-08-20): "prest0 just got better — refresh"
              when a new build ships; one click, lands exactly where they were.
              Never interrupts — the CTA waits while an answer is streaming. */}
          <AppUpdateBanner />
          {/* Mobile install offer (2026-09-08): on a phone, signed in, not yet
              installed — Android gets the native install sheet from a click,
              iOS Safari a short add-to-home-screen guide. Held back while an
              answer streams, never on the first page view, timestamped snoozes. */}
          <InstallAppBanner />
        </GlobalBannerCenter>
        {/* Trial onboarding lock-in: a mid-setup trial account is bounced
            from app surfaces back to /onboarding until the quick profile
            step completes (owner directive 2026-08-02). */}
        <TrialOnboardingGate />
        <PendingInviteGate />
        {/* A referral link opened on this device, waiting for the account made after it: back to its invitation page,
            where its start is claimed (a team invitation in progress comes first). */}
        <PendingReferralGate />
        {/* The one-time terms dialog (SOC 2 Phase 1, 2026-10-13): after the first paint, only for an account with no
            record of the current Terms and Privacy Policy, never over an answer still arriving; its chunk loads only
            when it may be shown. */}
        <TermsUpdateHost />
        <Suspense fallback={null}>
          <EnsureFixedAssets />
        </Suspense>
        </StandsDownForSignInSetup>
        <DockedContentShell>
        <Suspense fallback={<ResilientSuspenseFallback gateName="app-route-suspense" />}>
          <Routes>
            <Route
              path="/"
              element={SHOWS_NEW_HOME
                ? <PavilionHomeDoor fallback={<LandingPageDoor />} />
                : <LandingPageDoor />}
            />
            {/* /anonymous-demo removed */}
            
            {/* Business Pages */}


            {/* The partner firm's program page is retired (Miles, 2026-10-24): its address forwards to the person's own
                desktop, and what it said is the workspace's program record that Prest0n reads. */}
            <Route path="/justiceisurright" element={<Navigate to="/desktop" replace />} />
            <Route path="/team" element={<SafeTeamExpertise />} />
            <Route path="/about/team/team-expertise" element={<Navigate to="/team" replace />} />
            <Route path="/about/mission/project-mission" element={<SafeMission />} />
            <Route path="/mission" element={<SafeMission />} />
            <Route path="/insights" element={<SafeInsights />} />
            <Route path="/insights/*" element={<SafeInsights />} />
            <Route path="/roadmap" element={<SafeRoadmap />} />
            <Route path="/roadmap/*" element={<SafeRoadmap />} />
            <Route path="/changelog" element={<SafeChangelog />} />
            <Route path="/changelog/*" element={<SafeChangelog />} />
            <Route path="/blog/*" element={<Navigate to="/insights" replace />} />
            <Route path="/privacy-policy" element={<SafePrivacy />} />
            <Route path="/legal/privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms-of-service" element={<SafeTerms />} />
            <Route path="/legal/terms-of-service" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/cookie-policy" element={<SafeCookie />} />
            <Route path="/legal/cookie-policy" element={<Navigate to="/cookie-policy" replace />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/cookie" element={<Navigate to="/cookie-policy" replace />} />
            <Route path="/about" element={<SafeAbout />} />
            <Route path="/legal" element={<SafeLegal />} />
            {/* Trust Center (2026-09-11) — the subprocessor list and the plain
                statements a security questionnaire asks for. /trust is the name
                buyers type; /subprocessors is the one a DPA cites. */}
            <Route path="/trust" element={<SafeTrust />} />
            <Route path="/subprocessors" element={<Navigate to="/trust" replace />} />
            <Route path="/security" element={<SafeSecurity />} />
            {/* The open ROI reports, their calculator and the solutions pages (GSD revamp, 2026-10-15). Explicit
                addresses, no /roi/* wildcard: an unknown /roi/x reaches the 404, not the index. */}
            <Route path="/roi" element={<RoiInTheDesign />} />
            <Route path="/roi/law" element={<RoiInTheDesign />} />
            <Route path="/roi/marketing" element={<RoiInTheDesign />} />
            <Route path="/roi/finance" element={<RoiInTheDesign />} />
            <Route path="/roi/real-estate" element={<RoiInTheDesign />} />
            <Route path="/roi/insurance" element={<RoiInTheDesign />} />
            <Route path="/roi/property-management" element={<RoiInTheDesign />} />
            <Route path="/roi/general" element={<RoiInTheDesign />} />
            <Route path="/roi/methodology" element={<RoiInTheDesign />} />
            <Route path="/roi/data" element={<RoiInTheDesign />} />
            {/* The ROI report's own version page folded into what's new (2026-10-21: "one page/center for this purpose"). */}
            <Route path="/roi/changelog" element={<Navigate to="/changelog" replace />} />
            <Route path="/roi/calculator" element={<RoiCalculatorInTheDesign />} />
            <Route path="/roi/savings" element={<RoiSavingsInTheDesign />} />
            <Route path="/solutions/law" element={<SolutionsInTheDesign />} />
            <Route path="/solutions/marketing" element={<SolutionsInTheDesign />} />
            <Route path="/solutions/finance" element={<SolutionsInTheDesign />} />
            <Route path="/solutions/real-estate" element={<SolutionsInTheDesign />} />
            <Route path="/solutions/insurance" element={<SolutionsInTheDesign />} />
            <Route path="/solutions/property-management" element={<SolutionsInTheDesign />} />
            <Route path="/solutions/business" element={<SolutionsInTheDesign />} />
            {/* the page for marketing agencies and growing businesses until 2026-10-30: any business's page now */}
            <Route path="/solutions/professional" element={<Navigate to="/solutions/business" replace />} />
            {/* The booking calendar as a page (2026-10-13): what the "book a demo" dialog opens, at an address anyone
                can be sent. */}
            <Route path="/demo" element={<SafeBookDemo />} />
            <Route path="/wallet" element={<ProtectedRoute><WalletInTheDesign /></ProtectedRoute>} />
            <Route path="/activity" element={<ProtectedRoute><ActivityInTheDesign /></ProtectedRoute>} />
            {/* Prest0 Inc.'s engineering page: Prest0 Inc. only (DeveloperRoute's internal-org gate) and the queue's key, the
                widest of its views'; every view's callable is guarded again on the server. */}
            <Route path="/activity/engineering" element={<ProtectedRoute><DeveloperRoute permission="developer.escalations.view"><EngineeringInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            <Route path="/activity/engineering/*" element={<ProtectedRoute><DeveloperRoute permission="developer.escalations.view"><EngineeringInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            {/* /citadel (and its /prest0 alias) deleted entirely (Round M,
                2026-07-15 owner directive) — both now fall to the catch-all
                404 below. */}
            <Route path="/settings/team" element={<ProtectedRoute><TeamRolesInTheDesign /></ProtectedRoute>} />
            <Route path="/settings/skills" element={<ProtectedRoute><SkillsInTheDesign /></ProtectedRoute>} />
            <Route path="/settings/personas" element={<ProtectedRoute><PersonasInTheDesign /></ProtectedRoute>} />
            <Route path="/people" element={<ProtectedRoute><PeopleInTheDesign /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsInTheDesign /></ProtectedRoute>} />
            <Route path="/desktop" element={<ProtectedRoute><DesktopInTheDesign /></ProtectedRoute>} />
            {/* /agent-activity (2026-08-19): the path the signed browser
                deep link pointed at, and older "View Desktop" buttons open.
                The deep link was retired on 2026-10-06 (the page ignores a
                link's token and strips it from the address bar); the route
                stays so links already sent keep reaching the desktop. */}
            <Route path="/agent-activity" element={<ProtectedRoute><DesktopInTheDesign /></ProtectedRoute>} />
            <Route path="/preview/3d/:artifactId" element={<ProtectedRoute><Preview3DInTheDesign /></ProtectedRoute>} />
            <Route path="/browse" element={<Navigate to="/desktop" replace />} />
            <Route path="/integrations" element={<ProtectedRoute><IntegrationsInTheDesign /></ProtectedRoute>} />
            {/* The OAuth POPUP's landing must never bounce through auth
                guards (2026-08-14 owner repro: a brand-new trial user's
                popup got redirected off the callback before the minimal
                self-closing landing could render, and the sign-in window
                just stayed open on some other page). When the popup
                handshake mark is present, the callback renders the static
                landing directly — it shows no user data and closes itself.
                Without the mark (a full-window OAuth return), the guarded
                Integrations page renders exactly as before. */}
            <Route path="/integrations/callback" element={<IntegrationsCallbackRoute />} />
            <Route path="/pricing" element={<SafePricing />} />
            <Route path="/start" element={<StartTrialInTheDesign />} />
            <Route path="/trial" element={<StartTrialInTheDesign />} />
            {/* PRE-72: public SMS-consent signup + dedicated SMS-terms surface
                required to unblock Twilio A2P 10DLC + Toll-Free verification. */}
            <Route path="/signup" element={<SignupInTheDesign />} />
            <Route path="/sms-terms" element={<SmsTermsDoor />} />
            <Route path="/for-enterprise" element={<SafeForEnterprise />} />
            <Route path="/for-lawyers" element={<SafeForLawyers />} />
            <Route path="/for-individuals" element={<SafeForIndividuals />} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionInTheDesign /></ProtectedRoute>} />
            <Route path="/subscription/agreement" element={<ProtectedRoute><AgreementInTheDesign /></ProtectedRoute>} />
            <Route path="/subscription/trial" element={<ProtectedRoute><TrialStartInTheDesign /></ProtectedRoute>} />
            {/* The finish-setup screen (sign-in requirements, 2026-10-29): where the session gate sends a session that
                must finish setting up its sign-in; the one page such a session opens. */}
            <Route path="/sign-in-setup" element={<ProtectedRoute setupScreen><SignInSetupInTheDesign /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingInTheDesign /></ProtectedRoute>} />
            <Route path="/infrastructure" element={<ProtectedRoute><InfrastructureInTheDesign /></ProtectedRoute>} />
            {/* Developer tooling (catalog v4): fleet dashboard is developer-org-only —
                gated client-side here and server-side in rbacDevListFleetVms. */}
            <Route path="/admin/vms" element={<ProtectedRoute><DeveloperRoute permission="developer.fleet.view"><AdminVmsInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            {/* The insights studio: Prest0 Inc. only (DeveloperRoute's internal-org gate) and its write key; every call is
                guarded again on the server (functions/src/insights/insightsAccess.ts). */}
            <Route path="/admin/insights" element={<ProtectedRoute><DeveloperRoute permission="insights.posts.write"><InsightsStudioInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            <Route path="/admin/insights/*" element={<ProtectedRoute><DeveloperRoute permission="insights.posts.write"><InsightsStudioInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            {/* The roadmap studio: Prest0 Inc. only (DeveloperRoute's internal-org gate) and its view key; every call is
                guarded again on the server (functions/src/roadmap/roadmapAccess.ts). */}
            <Route path="/admin/roadmap" element={<ProtectedRoute><DeveloperRoute permission="roadmap.items.view"><RoadmapStudioInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            <Route path="/admin/roadmap/*" element={<ProtectedRoute><DeveloperRoute permission="roadmap.items.view"><RoadmapStudioInTheDesign /></DeveloperRoute></ProtectedRoute>} />
            <Route path="/portal-controls" element={<ProtectedRoute><PermissionRoute permission="account.view"><PortalControlsInTheDesign /></PermissionRoute></ProtectedRoute>} />

            {/* Invitation accept */}
            <Route path="/accept-invite" element={<AcceptInviteInTheDesign />} />

            {/* The referral program: its public page, a referral link's invitation (the long and the short link), the
                person's own referral page, where the terms are signed and the link lives, and the terms on their own page. */}
            <Route path="/referrals" element={<ReferralsInTheDesign />} />
            <Route path="/referrals/join/:code" element={<ReferralInviteInTheDesign />} />
            <Route path="/r/:code" element={<ReferralInviteInTheDesign />} />
            <Route path="/referrals/dashboard" element={<ProtectedRoute><ReferralDashboardInTheDesign /></ProtectedRoute>} />
            {/* The referral page with a tab of its "Prest0 Inc. only" section open (src/constants/prest0IncVocabulary.ts). */}
            <Route path="/referrals/dashboard/inc/:tab" element={<ProtectedRoute><ReferralDashboardInTheDesign /></ProtectedRoute>} />
            <Route path="/referral-terms" element={<ReferralTermsInTheDesign />} />

            {/* Authentication */}
            <Route path="/auth" element={<SafeAuthPage />} />
            {/* Branded handler for verification/reset email links (task #50). */}
            <Route path="/auth/action" element={<AuthActionInTheDesign />} />
            <Route path="/auth/callback" element={<AuthCallbackInTheDesign />} />

            {/* Legacy routes — redirect to /wallet */}
            <Route path="/dashboard" element={<Navigate to="/wallet" replace />} />
            <Route path="/admin" element={<Navigate to="/wallet" replace />} />

            {/* Protected Routes */}
            <Route path="/settings" element={<ProtectedRoute><SettingsInTheDesign /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfileInTheDesign /></ProtectedRoute>} />
            <Route path="/organization" element={<ProtectedRoute><OrganizationInTheDesign /></ProtectedRoute>} />

            {/* Moonshot demo route — renders the voice-enabled scripted chat */}
            <Route path="/moonshot" element={<MoonshotInTheDesign />} />
            <Route path="/moonshot/*" element={<MoonshotInTheDesign />} />

            {/* Catch all for 404 */}
            <Route path="*" element={<NotFoundInTheDesign />} />
          </Routes>
        </Suspense>
        </DockedContentShell>
        <StandsDownForSignInSetup>
        {/* App-level GlobalChatSystem — one instance for all pages */}
        <AppGlobalChat />
        {/* In‑app platform messages overlay integrated with prompt bar at bottom */}
        <InAppMessageOverlay />
        {/* The large in-page desktop sign-in surface (2026-08-18): chat's
            browser lane pops it over ANY page via prest0n:desktop-signin. */}
        <DesktopSignInModalHost />
        <ProgramWorkstationModalHost />
        {/* The "the window will say Composio" coach, over ANY page
            (2026-08-20): the shared connect engine dispatches
            prest0n:connect-coach around the sign-in window's lifecycle, so
            chat connects get the same coach as the Integrations page — and
            it hides when the sign-in window closes. */}
        <ComposioConnectCoachHost />
        {/* Which-inbox modal for CHAT connect clicks (C3b): the card's raw
            HTML buttons ask over the emailIntentChannel round-trip and this
            host renders the SAME EmailIntentPrompt the Integrations panel
            uses — one component, both surfaces. */}
        <EmailIntentPromptHost />
        {/* Setup-fields form for CHAT connect clicks (2026-08-24 WhatsApp
            repro): when an app needs initiation values before its sign-in
            (the WABA class), chat dispatches prest0n:connect-setup and this
            host opens the SAME SetupFieldsDialog the Integrations panel
            uses, right over the conversation. */}
        <ConnectSetupHost />
        {/* Sealed organization-change overlay (2026-08-19; pacing revised
            2026-08-20 — MIN_VISIBLE_MS floor overlapped with the work):
            every switch path announces the destination org full-screen and
            the boot continuation holds it up until the new workspace
            resolves — no mixed-org frame ever paints. One host, app-wide. */}
        <WorkspaceTransitionHost />
        </StandsDownForSignInSetup>
      </AppContainer>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <DemoMockGate>
      <AppWithProviders />
    </DemoMockGate>
  );
}

export default App; 